import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts/core'
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import {
  LineChart,
  BarChart,
  ScatterChart,
  PieChart,
  CandlestickChart
} from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

// Register ECharts components
echarts.use([
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent,
  LineChart,
  BarChart,
  ScatterChart,
  PieChart,
  CandlestickChart,
  CanvasRenderer
])

interface ChartContainerProps {
  type: 'line' | 'bar' | 'scatter' | 'pie' | 'doughnut' | 'candlestick' | 'histogram'
  title?: string
  height?: number
  data: any
  options?: any
  theme?: 'dark' | 'light'
}

export function ChartContainer({ 
  type, 
  title, 
  height = 300, 
  data, 
  options = {},
  theme = 'dark'
}: ChartContainerProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!chartRef.current) return

    // Initialize chart
    chartInstance.current = echarts.init(chartRef.current, theme)

    // Convert data based on chart type
    const chartOptions = generateChartOptions(type, data, options, title)
    
    chartInstance.current.setOption(chartOptions)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [type, data, options, title, theme])

  return (
    <div className="chart-container">
      <div 
        ref={chartRef} 
        style={{ width: '100%', height: `${height}px` }}
        className="chart-canvas"
      />
    </div>
  )
}

function generateChartOptions(type: string, data: any, options: any, title?: string) {
  const baseOptions = {
    title: title ? {
      text: title,
      textStyle: {
        color: 'var(--text-primary)',
        fontSize: 14,
        fontWeight: 500
      },
      left: 'left'
    } : undefined,
    tooltip: {
      trigger: 'item',
      backgroundColor: 'var(--bg-secondary)',
      borderColor: 'var(--border-primary)',
      textStyle: {
        color: 'var(--text-primary)'
      }
    },
    legend: {
      textStyle: {
        color: 'var(--text-secondary)'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    ...options
  }

  switch (type) {
    case 'line':
      return {
        ...baseOptions,
        xAxis: {
          type: 'category',
          data: data.labels,
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' },
          splitLine: { lineStyle: { color: 'var(--border-primary)', opacity: 0.3 } }
        },
        series: data.datasets.map((dataset: any) => ({
          name: dataset.label,
          type: 'line',
          data: dataset.data,
          smooth: true,
          lineStyle: { color: dataset.borderColor || 'var(--chart-primary)' },
          itemStyle: { color: dataset.backgroundColor || 'var(--chart-primary)' }
        }))
      }

    case 'scatter':
      return {
        ...baseOptions,
        xAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' },
          splitLine: { lineStyle: { color: 'var(--border-primary)', opacity: 0.3 } }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' },
          splitLine: { lineStyle: { color: 'var(--border-primary)', opacity: 0.3 } }
        },
        series: [{
          type: 'scatter',
          data: data.datasets[0].data.map((point: any) => [point.x, point.y]),
          itemStyle: {
            color: (params: any) => {
              const point = data.datasets[0].data[params.dataIndex]
              return point.color || 'var(--chart-primary)'
            }
          },
          tooltip: {
            formatter: (params: any) => {
              const point = data.datasets[0].data[params.dataIndex]
              return `${point.label}<br/>X: ${params.value[0]}<br/>Y: ${params.value[1]}`
            }
          }
        }]
      }

    case 'pie':
    case 'doughnut':
      return {
        ...baseOptions,
        series: [{
          type: 'pie',
          radius: type === 'doughnut' ? ['40%', '70%'] : '70%',
          data: data.labels.map((label: string, index: number) => ({
            name: label,
            value: data.datasets[0].data[index],
            itemStyle: {
              color: data.datasets[0].backgroundColor[index] || `var(--chart-color-${index})`
            }
          })),
          label: {
            color: 'var(--text-primary)'
          }
        }]
      }

    case 'histogram':
      // Simple histogram implementation
      const values = data.datasets[0].data
      const bins = 10
      const min = Math.min(...values)
      const max = Math.max(...values)
      const binWidth = (max - min) / bins
      
      const histogram = Array(bins).fill(0)
      values.forEach((value: number) => {
        const binIndex = Math.min(Math.floor((value - min) / binWidth), bins - 1)
        histogram[binIndex]++
      })

      return {
        ...baseOptions,
        xAxis: {
          type: 'category',
          data: Array(bins).fill(0).map((_, i) => `${(min + i * binWidth).toFixed(2)}`),
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' },
          splitLine: { lineStyle: { color: 'var(--border-primary)', opacity: 0.3 } }
        },
        series: [{
          type: 'bar',
          data: histogram,
          itemStyle: { color: 'var(--chart-primary)' }
        }]
      }

    case 'candlestick':
      return {
        ...baseOptions,
        xAxis: {
          type: 'category',
          data: data.labels,
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: 'var(--border-primary)' } },
          axisLabel: { color: 'var(--text-muted)' },
          splitLine: { lineStyle: { color: 'var(--border-primary)', opacity: 0.3 } }
        },
        series: [{
          type: 'candlestick',
          data: data.datasets[0].data,
          itemStyle: {
            color: 'var(--chart-up)',
            color0: 'var(--chart-down)',
            borderColor: 'var(--chart-up)',
            borderColor0: 'var(--chart-down)'
          }
        }]
      }

    default:
      return baseOptions
  }
}